import React, { ChangeEvent, useEffect, useState } from "react";
import Button from "../../common/forms/button";
import { clickToLinkHandler } from "../../../helpers/utils";
import {
  configureAuthHeader,
  configureBaseUrl,
} from "../../../api/axiosConfig";
import * as api from "../../../api/generated-api";
import {
  getAllDealLegalEntityInvestmentsForDeal,
  getDealInvestor,
  getDealInvestorMembers,
} from "../../../api/deals/investors";
import {
  Box,
  Divider,
  FormControl,
  Grid,
  SelectChangeEvent,
  Stack,
  Typography,
} from "@mui/material";
import RemoveIcon from "../../common/icons/removeIcon";
import COLORS from "../../../colors";
import DKFormContainer from "../../common/forms/formContainer/DKFormContainer";
import DKFormInputLabelContainer from "../../common/widgets/formVariablesWidget/formInputContainer/DKFormInputLabelContainer";
import DKFormToggleButtonGroup from "../../common/forms/DKFormToggleButtonGroup";
import "./../documentSignature/styles/SignatureBlock.scss"; // this is necessary to apply appropriate styles to the signature block preview
import { getDealLegalEntitiesForDeal } from "../../../api/deals/deallegalentities";
import { DealsPageLayout } from "../dealsPageLayout/DealsPageLayout";
import DealPageTitleAndAction from "../common/DealPageTitleAndAction";
import { useHistory } from "react-router-dom";
import {
  createSignatureBlockWithContentPreview,
  getSignatureBlock,
  updateSignatureBlock,
} from "../../../api/deals/signatureBlocks";
import { getDisplayNameForLegalEntityType } from "../../../helpers/utilsTS";
import DKFormTextInput from "../../common/widgets/formVariablesWidget/DKFormTextInput";
import DKFormSelect from "../../common/forms/DKFormSelect";

type SignatureBlockConfigurationPageProps = {
  token: string;
  orgId: number;
  dealId: number;
  dealLegalEntityId: number;
  dealLegalEntityInvestmentId?: number;
};

const SignatureBlockConfigurationPage = ({
  token,
  orgId,
  dealId,
  dealLegalEntityId,
  dealLegalEntityInvestmentId,
}: SignatureBlockConfigurationPageProps) => {
  configureBaseUrl();
  configureAuthHeader(token);

  const defaultSignatureBlock: api.SignatureBlockCreate = {
    isEntity: true,
    title: "",
    intermediateBy: "",
    intermediateIts: "",
  };

  const [potentialSigners, setPotentialSigners] = useState<
    api.DealLegalEntity[]
  >([]);
  const [signatureBlock, setSignatureBlock] = useState<
    api.SignatureBlock | api.SignatureBlockUpdate
  >(defaultSignatureBlock);
  const [signatureBlockForUpdate, setSignatureBlockForUpdate] = useState<
    api.SignatureBlock | api.SignatureBlockUpdate
  >(defaultSignatureBlock);
  const [intermediateVisible, setIntermediateVisible] =
    useState<boolean>(false);
  const [signatureBlockContent, setSignatureBlockContent] =
    useState<string>("");
  const [isInvestorSigner, setIsInvestorSigner] = useState<boolean>(false);
  const [dealLegalEntityForSigner, setDealLegalEntityForSigner] =
    useState<api.DealLegalEntity>();
  const [
    dealLegalEntityInvestmentForSigner,
    setDealLegalEntityInvestmentForSigner,
  ] = useState<api.DealLegalEntityInvestment>();
  const [dealInvestorForSigner, setDealInvestorForSigner] =
    useState<api.DealInvestor>();

  const history = useHistory();

  useEffect(() => {
    getDealLegalEntitiesForDeal(orgId, dealId, (dealLegalEntities) => {
      // Get the deal legal entity for the signature block
      const dealLegalEntityForSignatureBlock = dealLegalEntities.find(
        (dealLegalEntity) => dealLegalEntity.id == dealLegalEntityId // dealLegalEntityId is a string so using == instead of ===
      );

      setDealLegalEntityForSigner(dealLegalEntityForSignatureBlock);

      // If the deal legal entity if an investor
      if (
        dealLegalEntityForSignatureBlock.isInvestor &&
        dealLegalEntityInvestmentId
      ) {
        // Set the flag to indicate that the signer is an investor signer
        setIsInvestorSigner(true);

        // Get the currently selected signer
        getAllDealLegalEntityInvestmentsForDeal(
          orgId,
          dealId,
          (dealLegalEntityInvestments) => {
            const dealLegalEntityInvestmentForSigner =
              dealLegalEntityInvestments.find(
                (dealLegalEntityInvestment) =>
                  dealLegalEntityInvestment.id === dealLegalEntityInvestmentId
              );

            setDealLegalEntityInvestmentForSigner(
              dealLegalEntityInvestmentForSigner
            );

            // Get the deal investor members for the signer
            getDealInvestorMembers(
              orgId,
              dealId,
              dealLegalEntityInvestmentForSigner.investorId,
              (dealInvestorMembers) => {
                // Filter the deal investor members to only include investor members not the investor investments
                const filteredDealInvestorMembers = dealInvestorMembers.filter(
                  (dealInvestorMember) =>
                    dealInvestorMember.legalEntityType ===
                    api.InvestorLegalEntityType.Investor_Member
                );

                setPotentialSigners(filteredDealInvestorMembers);
              }
            );

            // Get the deal investor for the signer
            getDealInvestor(
              orgId,
              dealId,
              dealLegalEntityInvestmentForSigner.investorId,
              (dealInvestor) => {
                setDealInvestorForSigner(dealInvestor);
              }
            );
          }
        );
      }

      getSignatureBlock(orgId, dealId, dealLegalEntityId, (signatureBlock) => {
        setSignatureBlock(signatureBlock);
        setSignatureBlockForUpdate(signatureBlock);
        setIntermediateVisible(
          signatureBlock.intermediateBy &&
            dealLegalEntityForSignatureBlock.legalEntityType ===
              api.InvestorLegalEntityType.Investor_Investment
        );
      });
    });
  }, [orgId, dealId, dealLegalEntityId, dealLegalEntityInvestmentId]);

  useEffect(() => {
    if (
      isInvestorSigner &&
      potentialSigners.length > 0 &&
      dealLegalEntityInvestmentForSigner
    ) {
      const selectedDealLegalEntityAsSigner = potentialSigners.find(
        (dealLegalEntity) => {
          return (
            dealLegalEntity.person.id ===
            dealLegalEntityInvestmentForSigner.signer.person.id
          );
        }
      );

      setSignatureBlockForUpdate({
        ...signatureBlockForUpdate,
        personIdOfSigner: selectedDealLegalEntityAsSigner.person.id,
      });
    }
  }, [isInvestorSigner, potentialSigners, dealLegalEntityInvestmentForSigner]);

  useEffect(() => {
    if (dealLegalEntityForSigner && signatureBlockForUpdate) {
      createSignatureBlockWithContentPreview(
        orgId,
        dealId,
        dealLegalEntityId,
        signatureBlockForUpdate.isEntity,
        `${dealLegalEntityForSigner.person.firstName} ${dealLegalEntityForSigner.person.lastName}`,
        dealLegalEntityForSigner.person.email,
        signatureBlockForUpdate.title ?? "",
        signatureBlockForUpdate.intermediateBy ?? "",
        signatureBlockForUpdate.intermediateIts ?? "",
        (createdSignatureBlock) => {
          setSignatureBlockContent(createdSignatureBlock.content);
        }
      );
    }
  }, [dealLegalEntityForSigner, signatureBlockForUpdate]);

  const checkValidIntermediateSignatory = () => {
    if (
      signatureBlockForUpdate.intermediateIts &&
      !signatureBlockForUpdate.intermediateBy
    ) {
      return false;
    }
    return true;
  };

  const checkCanSave = () => {
    if (signatureBlock === signatureBlockForUpdate) {
      return false;
    }

    return (
      (signatureBlockForUpdate.isEntity && checkValidIntermediateSignatory()) ||
      !signatureBlockForUpdate.isEntity
    );
  };

  const handleUpdate = () => {
    updateSignatureBlock(
      orgId,
      dealId,
      dealLegalEntityId,
      {
        isEntity: signatureBlockForUpdate.isEntity,
        title: signatureBlockForUpdate.title ?? "",
        intermediateBy: signatureBlockForUpdate.intermediateBy ?? "",
        intermediateIts: signatureBlockForUpdate.intermediateIts ?? "",
        dealLegalEntityInvestmentId: isInvestorSigner
          ? dealLegalEntityInvestmentId
          : undefined,
        personIdOfSigner: isInvestorSigner
          ? signatureBlockForUpdate.personIdOfSigner
          : undefined,
      },
      () => {
        // Navigate the user back to the page the were viewing before they came here
        history.goBack();
      }
    );
  };

  const handleSaveButtonClick = () => {
    if (checkCanSave()) {
      handleUpdate();
    }
  };

  const handleTitleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSignatureBlockForUpdate({
      ...signatureBlockForUpdate,
      title: e.target.value,
    });
  };

  const handleByChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSignatureBlockForUpdate({
      ...signatureBlockForUpdate,
      intermediateBy: e.target.value,
    });
  };

  const handleItsChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSignatureBlockForUpdate({
      ...signatureBlockForUpdate,
      intermediateIts: e.target.value,
    });
  };

  const handleSignerTypeChange = (isEntity: boolean) => {
    if (isEntity) {
      setSignatureBlockForUpdate({
        ...signatureBlockForUpdate,
        isEntity: true,
      });
    } else {
      setSignatureBlockForUpdate({
        ...signatureBlockForUpdate,
        title: "",
        intermediateBy: "",
        intermediateIts: "",
        isEntity: false,
      });
    }
  };

  const handleSignerSelectChange = (event: SelectChangeEvent) => {
    const chosenId = Number(event.target.value);

    if (chosenId === 0) {
      clickToLinkHandler(
        `/${orgId}/deals/${dealId}/edit/investors/edit/${dealLegalEntityInvestmentForSigner.investorId}`
      );
    } else {
      const chosenDealInvestorMember = potentialSigners.filter((member) => {
        if (member.person.id === chosenId) {
          return member;
        }
      })[0];

      setSignatureBlockForUpdate({
        ...signatureBlockForUpdate,
        personIdOfSigner: chosenDealInvestorMember.person.id,
      });

      setDealLegalEntityForSigner(chosenDealInvestorMember);
    }
  };

  const onRemoveIntermediate = () => {
    setIntermediateVisible(false);
    setSignatureBlockForUpdate({
      ...signatureBlockForUpdate,
      intermediateBy: undefined,
      intermediateIts: undefined,
    });
  };

  if (
    !dealLegalEntityForSigner ||
    !signatureBlock ||
    !signatureBlockForUpdate
  ) {
    return null;
  }

  if (
    dealLegalEntityInvestmentId &&
    (!dealInvestorForSigner || !dealLegalEntityInvestmentForSigner)
  ) {
    return null;
  }

  const selectSignerItems = potentialSigners.map((dealLegalEntity) => {
    return {
      value: dealLegalEntity.person.id,
      label: `${dealLegalEntity.person.firstName} ${dealLegalEntity.person.lastName}`,
    };
  });
  selectSignerItems.push({ value: 0, label: "Add New" });

  const getBreadcrumbs = () => {
    let breadcrumbs = [
      {
        title: "Company Information",
        path: `/${orgId}/deals/${dealId}/edit/company-information`,
      },
    ];

    if (dealLegalEntityForSigner.isInvestor) {
      breadcrumbs = [
        {
          title: "Investors",
          path: `/${orgId}/deals/${dealId}/edit/investors`,
        },
        {
          title: `${dealInvestorForSigner.name}`,
          path: `/${orgId}/deals/${dealId}/edit/investors/edit/${dealLegalEntityInvestmentForSigner.investorId}`,
        },
      ];
    }

    return breadcrumbs;
  };

  return (
    <DealsPageLayout
      showDealNav={false}
      leftHeaderAction={
        <DealPageTitleAndAction
          title={
            dealLegalEntityInvestmentForSigner
              ? dealLegalEntityInvestmentForSigner.name
              : dealLegalEntityForSigner.name
          }
          subtitle={getDisplayNameForLegalEntityType(
            dealLegalEntityForSigner.legalEntityType
          )}
          breadcrumbs={getBreadcrumbs()}
        />
      }
    >
      <Box
        sx={{
          maxWidth: "1400px",
        }}
      >
        <DKFormContainer>
          <Grid container columnSpacing={10} rowSpacing={3}>
            <Grid item xs={6}>
              <Typography variant="h3">Configure Signature Block</Typography>
            </Grid>
            <Grid item xs={6}>
              <Stack
                direction={"row"}
                spacing={1}
                sx={{
                  justifyContent: "flex-end",
                }}
              >
                {isInvestorSigner &&
                  !intermediateVisible &&
                  signatureBlockForUpdate.isEntity && (
                    <Button
                      id="add-intermediate"
                      data-testId={"add-intermediate-signatory"}
                      type="secondary"
                      size="medium"
                      onClick={() => {
                        setIntermediateVisible(true);
                      }}
                    >
                      Add Intermediate
                    </Button>
                  )}
                <Button
                  data-testId={"save-signature-block"}
                  type={(checkCanSave() && "primary") || "disabled-primary"}
                  size="medium"
                  onClick={handleSaveButtonClick}
                  modifier={undefined}
                  itemLeft={undefined}
                >
                  save
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item md={12} lg={6}>
              <Stack spacing={3}>
                {isInvestorSigner && (
                  <DKFormToggleButtonGroup
                    label="Signer Type"
                    items={[
                      {
                        dataTestId: "mark-as-entity-button",
                        value: true,
                        label: "Entity",
                      },
                      {
                        dataTestId: "mark-as-individual-button",
                        value: false,
                        label: "Individual",
                      },
                    ]}
                    selectedValue={signatureBlockForUpdate.isEntity}
                    onChange={(value) =>
                      handleSignerTypeChange(value as boolean)
                    }
                  />
                )}
                {signatureBlockForUpdate.personIdOfSigner &&
                  signatureBlockForUpdate.isEntity &&
                  !!potentialSigners.length && (
                    <DKFormSelect
                      dataTestId="select-signer-person"
                      label="Choose Person to Sign"
                      items={selectSignerItems}
                      selectedValue={signatureBlockForUpdate.personIdOfSigner}
                      onChange={handleSignerSelectChange}
                    />
                  )}
                {signatureBlockForUpdate.isEntity && (
                  <>
                    <DKFormTextInput
                      dataTestId="signature-block-title-field"
                      label="Signer Title"
                      name="title"
                      type="text"
                      fullWidth={true}
                      onChange={handleTitleChange}
                      value={signatureBlockForUpdate.title}
                      variant="outlined"
                    />
                    {intermediateVisible && (
                      <>
                        <FormControl
                          component="fieldset"
                          title="Intermediate Signatory"
                        >
                          <Box
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              flexDirection: "row",
                            }}
                          >
                            <Typography>Intermediate Signatory</Typography>
                            <Box
                              style={{ cursor: "pointer" }}
                              data-testid={"remove-intermediate"}
                              onClick={() => {
                                onRemoveIntermediate();
                              }}
                            >
                              <RemoveIcon
                                style={{ cursor: "pointer" }}
                                fill={COLORS.SECONDARY_COLOR_700}
                                onClick={onRemoveIntermediate}
                              />
                            </Box>
                          </Box>
                          <DKFormTextInput
                            customErrorLabel={
                              signatureBlockForUpdate.intermediateIts &&
                              !signatureBlockForUpdate.intermediateIts
                                ? '"By" is required for intermediate signatories'
                                : undefined
                            }
                            dataTestId="signature-block-by-field"
                            label="By"
                            name="intermediateBy"
                            type="text"
                            fullWidth={true}
                            onChange={handleByChange}
                            value={signatureBlockForUpdate.intermediateBy}
                            variant="outlined"
                          />
                          <DKFormTextInput
                            dataTestId="signature-block-its-field"
                            className="deals-input signer-field intermediate-field-last"
                            label="Its"
                            name="intermediateIts"
                            type="text"
                            fullWidth={true}
                            onChange={handleItsChange}
                            value={signatureBlockForUpdate.intermediateIts}
                            variant="outlined"
                          />
                        </FormControl>
                      </>
                    )}
                  </>
                )}
              </Stack>
            </Grid>
            <Grid item md={12} lg={6}>
              <Box>
                <DKFormInputLabelContainer label="Signature Block Preview">
                  <DKFormContainer>
                    <Box
                      dangerouslySetInnerHTML={{
                        __html: signatureBlockContent,
                      }}
                    ></Box>
                  </DKFormContainer>
                </DKFormInputLabelContainer>
              </Box>
            </Grid>
          </Grid>
        </DKFormContainer>
      </Box>
    </DealsPageLayout>
  );
};

export default SignatureBlockConfigurationPage;
